// @ts-nocheck
'use client'

import { useState, useEffect } from 'react'
import { PlusIcon, PencilSquareIcon, TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import TableComponent from 'components/common/TableComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import GroupModal from 'components/modals/GroupModal'
import ActionCardComponent from 'components/common/ActionCardComponent'
import TooltipComponent from 'components/common/TooltipComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import ModalComponent from 'components/common/ModalComponent'

import { useAuth } from 'auth/AuthContext'

const GroupsManagement = ({ onBlockGroupsUpdate }) => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const [state, setState] = useState({
    blockGroups: [],
    error: null,
    isOpenGroupModal: false,
    selectedGroup: null,
    groupsSearchTerm: '',
    loading: true,
    showDeleteConfirm: false,
    pendingDelete: null
  })

  const getBlockGroups = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/farming-blocks/block-groups/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })

      const groups = Array.isArray(response.data) ? response.data : []
      setState(s => ({
        ...s,
        blockGroups: groups,
        error: null,
        loading: false
      }))
      // Notify parent component of block groups update
      if (onBlockGroupsUpdate) {
        onBlockGroupsUpdate(groups)
      }
    } catch (err) {
      showAlert({ message: t('mgmt.groups.errLoad'), icon: 'error' })
      setState(s => ({
        ...s,
        error: t('mgmt.groups.errLoad'),
        blockGroups: [],
        loading: false
      }))
    }
  }

  const handleSaveGroup = async (groupData) => {
    try {
      const isEditing = !!state?.selectedGroup?.blockGroupId

      const method = isEditing ? 'PUT' : 'POST'
      const url = isEditing
        ? `/management/farming-blocks/block-groups/${state.selectedGroup.blockGroupId}`
        : `/management/farming-blocks/block-groups/${user.farmId}`

      const response = await makeRequest({
        method,
        url,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        data: groupData,
        alertResult: true
      })

      if (response?.status === 200 || response?.status === 201) {
        await getBlockGroups()
        setState(s => ({
          ...s,
          isOpenGroupModal: false,
          selectedGroup: null
        }))
      }
    } catch (err) {
      showAlert({ message: t('mgmt.groups.errSave'), icon: 'error' })
      setState(s => ({
        ...s,
        error: t('mgmt.groups.errSave')
      }))
    }
  }

  const handleDelete = (group) => {
    setState(s => ({ ...s, showDeleteConfirm: true, pendingDelete: group }))
  }

  const confirmDelete = async () => {
    const group = state.pendingDelete
    setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))
    try {
      const response = await makeRequest({
        method: 'DELETE',
        url: `/management/farming-blocks/block-groups/${group.blockGroupId || group.groupId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        alertResult: true
      })
      if (response?.status === 200) {
        getBlockGroups()
      }
    } catch (error) {
      showAlert({ text: t('mgmt.groups.errDelete'), icon: 'error' })
    }
  }

  const onToogleGroupModal = (group = null) => {
    setState(s => ({
      ...s,
      isOpenGroupModal: !s.isOpenGroupModal,
      selectedGroup: group
    }))
  }

  const filteredGroups = state.blockGroups.filter(group =>
    (group.groupName || '').toLowerCase().includes(state.groupsSearchTerm)
  )

  const groupsTableData = {
    columns: [
      { key: 'blockGroupId', labelKey: 'mgmt.groups.cols.number' },
      { key: 'groupName', labelKey: 'mgmt.groups.cols.groupName' },
      {
        key: 'actions',
        labelKey: 'mgmt.groups.cols.manage',
        cell: (value, group) => (
          <div className='flex items-center gap-2'>
            <button onClick={() => onToogleGroupModal(group)} title={t('common.edit')} className='p-1 rounded transition'>
              <PencilSquareIcon className='w-5 h-5 text-blue-500' />
            </button>
            <button onClick={() => handleDelete(group)} title={t('common.delete')} className='p-1 rounded transition'>
              <TrashIcon className='w-5 h-5 text-red-500' />
            </button>
          </div>
        )
      }
    ],
    rows: filteredGroups
  }

  useEffect(() => {
    if (!user?.farmId) return
    getBlockGroups()
  }, [user?.farmId])

  if (state.loading) {
    return <PageLoadingState message={t('mgmt.groups.loading')} rows={4} />
  }

  return (
    <>
      <ActionCardComponent>
        <input
          type='text'
          value={state.groupsSearchTerm}
          placeholder={t('mgmt.groups.filter')}
          onChange={(e) => setState(s => ({ ...s, groupsSearchTerm: e.target.value.toLowerCase() }))}
          className='search-input'
          data-align='left'
        />

        {!!user.farmId && (
          <TooltipComponent content={t('mgmt.groups.tooltip')}>
            <ButtonComponent onClick={() => onToogleGroupModal()}>
              <PlusIcon className='w-5 h-5 mr-1' />
              {t('mgmt.groups.new')}
            </ButtonComponent>
          </TooltipComponent>
        )}
      </ActionCardComponent>

      <TableComponent tableData={groupsTableData} />

      {/* group modal */}
      {state.isOpenGroupModal && (
        <GroupModal
          isOpen
          handleClose={onToogleGroupModal}
          handleSave={handleSaveGroup}
          selectedGroup={state.selectedGroup}
        />
      )}

      <ModalComponent
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
        title={t('mgmt.groups.deleteTitle')}
        maxWidth='sm'
        hideDefaultFooter
      >
        <div className='flex flex-col items-center gap-4 py-2'>
          <ExclamationTriangleIcon className='w-12 h-12 text-red-400' />
          <p className='text-sm text-gray-700 text-center'>
            {t('mgmt.groups.removeConfirm', { name: state.pendingDelete?.groupName ?? '' })}
            <br />
            <span className='text-gray-500'>{t('mgmt.common.confirmDelete')}</span>
          </p>
          <div className='flex justify-end gap-3 w-full mt-2'>
            <button
              type='button'
              onClick={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 transition'
            >
              {t('mgmt.common.cancel')}
            </button>
            <button
              type='button'
              onClick={confirmDelete}
              className='px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition'
            >
              {t('mgmt.common.delete')}
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  )
}

export default GroupsManagement
